import {
  type ChurchNavSection } from "@/features/dashboard/model/church-nav"
import { TenantAuthorizationAction } from "@/features/auth/tenant-authorization/model/tenant-authorization.types"
import { FINANCE_ACCOUNTS_FUNDS_URLS } from "@/features/finance/model/accounts-funds-tabs"

/**
 * The Parish sidebar. When the active branch is the SYSTEM/parish branch (slug "parish"), this IS the sidebar —
 * there is no branch sidebar at parish level. Drilling into a branch from the Branches list opens a tabbed
 * Branch Details console instead (see the branch-details routes), not another sidebar.
 *
 * These are branch-scoped items (rendered through the same `toItem` mapping + mobile nav as CHURCH_NAV_ITEMS),
 * so their URLs resolve under `/w/:ws/b/parish/...`. "My Organization" is NOT here — it stays as the sidebar
 * footer item (unchanged), the same workspace-scoped `/w/:ws/organization` entry it already was.
 *
 * Budgets and Audit Log are navigation entries that render a Coming Soon placeholder for now.
 */
export const PARISH_NAV_ITEMS: ChurchNavSection[] = [
  {
    titleKey: "parishNav.sections.overview",
    url: "/",
    iconKey: "home",
    match: "exact",
    mobileMenuGroup: "navigation",
    items: [],
  },
  {
    titleKey: "parishNav.sections.branches",
    url: "/branches",
    iconKey: "branch",
    mobileMenuGroup: "navigation",
    items: [],
  },
  {
    titleKey: "parishNav.sections.activities",
    url: "/activities",
    iconKey: "calendarDays",
    mobileMenuGroup: "navigation",
    items: [],
  },
  {
    titleKey: "parishNav.sections.ministries",
    url: "/ministries",
    iconKey: "heartHandshake",
    mobileMenuGroup: "navigation",
    items: [],
  },
  {
    /**
     * PARISH Finance — the parish AS A BRANCH: its own transactions, transfers, reconciliation, reports.
     *
     * Named "Parish Finance" rather than "Finance" because it now sits beside Global Finance, and the pair only
     * makes sense if each says whose money it is. One section held both scopes before, which made the
     * church-wide view look like a subsection of the parish's own books when it is the wider one.
     */
    titleKey: "parishNav.sections.parishFinance",
    url: "/finance",
    iconKey: "wallet",
    mobileMenuGroup: "navigation",
    items: [
      { titleKey: "churchNav.sections.finance.overview", url: "/finance", enabled: true, match: "exact", mobileMenuGroup: "navigation" },
      { titleKey: "churchNav.sections.finance.transactions", url: "/finance/transactions", enabled: true, mobileMenuGroup: "navigation" },
      /*
       * The parish's OWN accounts and funds — one entry opening a two-tab page, exactly as the branch nav does.
       *
       * This was removed when the Chart of Accounts and consolidated Funds moved to Global Finance. That was
       * right for those two: the chart is workspace-owned and the consolidated fund view spans every branch.
       * But `/finance/accounts` is a SEGMENTED page, and it took the parish's FINANCIAL accounts — its own bank
       * and cash balances — along with them. Those belong to the branch that holds them, and the parish is a
       * branch. With the page's own tab bar standing down in sidebar layout, removing the link left the parish
       * treasurer with no way at all to see how much the parish holds.
       */
      { titleKey: "churchNav.sections.finance.accountsFunds", url: "/finance/accounts", matchUrls: FINANCE_ACCOUNTS_FUNDS_URLS, enabled: true, match: "prefix", mobileMenuGroup: "navigation" },
      // The SAME item as the branch nav, under the same name and pointing at the same screen.
      //
      // This used to read "Remittances" and point at `/finance/remittances`, a ComingSoon placeholder — so the
      // parish, which is the side that APPROVES every incoming transfer, had no way to reach the transfers
      // screen at all, while branches saw it as "Fund Transfers". One feature, one label, one URL.
      { titleKey: "churchNav.sections.finance.transfers", url: "/finance/transfers", enabled: true, match: "prefix", mobileMenuGroup: "navigation" },
      // IBA-161. The parish reconciles its own accounts exactly as a branch does, and is often where the
      // reviewing happens — so the queue must be reachable here too, not only from a branch.
      { titleKey: "churchNav.sections.finance.reconciliation", url: "/finance/reconciliation", enabled: true, match: "prefix", mobileMenuGroup: "navigation" },
      // NO Reports entry here, and none under Global Finance either: reporting is its own top-level section
      // now. There is one catalog in the product, so a "Reports" inside a money section could only mean a
      // narrower, differently-scoped one — which is exactly the ambiguity this comment used to describe.
      // Investigations at parish scope. The page already resolves PARISH to the SYSTEM branch alone
      // (`resolveScope` in finance-investigation.service), so it shows the parish's own money and reveals
      // nothing about other branches — it was simply missing from this nav.
      { titleKey: "churchNav.sections.finance.investigations", url: "/finance/investigations", enabled: true, match: "prefix", mobileMenuGroup: "navigation" },
      { titleKey: "churchNav.sections.finance.settings", url: "/finance/settings", enabled: true, mobileMenuGroup: "navigation" },
    ],
  },
  {
    /**
     * GLOBAL Finance — the WORKSPACE's money: every branch plus the parish, consolidated.
     *
     * A section of its own, directly after Parish Finance. It is a different scope, not a deeper level of the
     * parish's books, and as a child item it read as the latter.
     *
     * Its destinations are sidebar CHILDREN here, mirroring Parish Finance — the page's own tab bar stands down
     * in sidebar layout (`GlobalFinanceNav`, the same rule `FinanceSectionNav` follows), so there is still only
     * one visible set of entry points at a time.
     *
     * Visibility is decided by the centralized tenant authorization registry in
     * `filterParishNavSections`; parish navigation does not use the branch-nav filter.
     */
    titleKey: "globalFinance.nav.section",
    url: "/global-finance",
    iconKey: "building",
    match: "prefix",
    mobileMenuGroup: "navigation",
    items: [
      { titleKey: "globalFinance.nav.overview", url: "/global-finance/overview", enabled: true, match: "prefix", mobileMenuGroup: "navigation" },
      { titleKey: "globalFinance.nav.chartOfAccounts", url: "/global-finance/chart-of-accounts", enabled: true, match: "prefix", mobileMenuGroup: "navigation" },
      { titleKey: "globalFinance.nav.funds", url: "/global-finance/funds", enabled: true, match: "prefix", mobileMenuGroup: "navigation" },
    ],
  },
  {
    // Reports — top level here too. Same reasoning as the branch nav: reports belong to no single module,
    // and the consolidated catalog that used to sit under Global Finance is now a SCOPE, not a place.
    titleKey: "churchNav.sections.reports.title",
    url: "/reports",
    iconKey: "chartColumn",
    // No badge here any more. "New" is now an operator decision in Admin -> Features, carried by a
    // seeded row, so retiring it after the release is a toggle rather than a deploy.
    mobileMenuGroup: "navigation",
    items: [],
  },
  {
    titleKey: "parishNav.sections.budgets",
    url: "/budgets",
    iconKey: "pieChart",
    mobileMenuGroup: "administration",
    items: [],
  },
  {
    titleKey: "parishNav.sections.auditLog",
    url: "/audit-log",
    iconKey: "clipboardList",
    mobileMenuGroup: "administration",
    items: [],
  },
]

/**
 * Parish nav, minus anything the viewer must not see.
 *
 * Global Finance is church-wide money: a single-branch treasurer must not see it, and in a mono workspace it
 * does not exist at all (the one branch IS the workspace, so there is nothing to consolidate). The API remains
 * the security authority; this registry-backed filter decides which doors are visible.
 */
export type ParishNavigationAuthorization = {
  can: (action: TenantAuthorizationAction) => boolean
}

function parishActionForUrl(url: string): TenantAuthorizationAction {
  switch (url) {
    case "/branches": return TenantAuthorizationAction.BranchesManage
    case "/activities": return TenantAuthorizationAction.ActivitiesView
    case "/ministries": return TenantAuthorizationAction.MinistriesView
    // This is the parish's finance configuration (accounts, fiscal periods,
    // receipts), not workspace governance. It must use the same FINANCE view
    // decision as the other parish-finance destinations; requiring SETTINGS
    // here hid a valid finance page from parish treasurers.
    case "/finance/settings": return TenantAuthorizationAction.FinanceView
    case "/finance":
    case "/finance/transactions":
    case "/finance/accounts":
    case "/finance/transfers":
    case "/finance/reconciliation":
    case "/finance/investigations":
    case "/global-finance":
    case "/global-finance/overview":
    case "/global-finance/chart-of-accounts":
    case "/global-finance/funds": return TenantAuthorizationAction.FinanceView
    case "/reports": return TenantAuthorizationAction.ReportsView
    // Paused but still protected: Coming Soon is not a permission bypass.
    case "/budgets": return TenantAuthorizationAction.BudgetingView
    case "/audit-log": return TenantAuthorizationAction.AuditLogView
    default: return TenantAuthorizationAction.DashboardView
  }
}

/** Every parish item is filtered through the central authorization registry; no feature-specific bypasses. */
export function filterParishNavSections(
  sections: ChurchNavSection[],
  options: { authorization: ParishNavigationAuthorization; showMultiBranchWorkspaceUi: boolean },
): ChurchNavSection[] {
  return sections.filter((section) => {
    if (section.url === "/global-finance" && !options.showMultiBranchWorkspaceUi) return false
    return options.authorization.can(parishActionForUrl(section.url))
  }).map((section) => ({
    ...section,
    items: section.items.filter((item) => options.authorization.can(parishActionForUrl(item.url))),
  }))
}
